import { Extension, InputRule } from '@tiptap/core';
import type { EditorState } from '@tiptap/pm/state';
import { emojiByShortcode } from '../data/emoji';

/**
 * Gepeles kozbeni emoji-atalakitas.
 *
 * Ket dolgot alakit at, mindkettot ABBAN A PILLANATBAN, amikor keszen van:
 *
 *   - a bevett emotikonokat: `:)` -> 🙂, `xD` -> 😆, `<3` -> ❤️
 *   - a rovidneveket: `:salute:` -> 🫡
 *
 * ─── Miert azonnal, es nem kuldeskor? ───────────────────────────────────────
 *
 * Mert amit atalakitunk, azt latni kell, MIELOTT elmegy. Kuldeskori csere
 * eseten a felhasznalo egy olyan uzenetet kuldene el, amit nem irt le - es a
 * visszavonasra sem lenne mar lehetosege. Igy viszont ott all a jel a
 * szerkesztoben: ha nem tetszik, egy Ctrl+Z visszahozza a begepelt alakot.
 *
 * ─── A BAL HATAR mindenhol kotelezo ─────────────────────────────────────────
 *
 * Minden minta csak sor elejen vagy szokoz utan indul. Enelkul a `https://`
 * kozepen allo `:/` mosolyt csinalna a linkbol, es minden `d`-re vegzodo szo
 * utani kettospont gyanussa valna. Ez a szabaly teszi a dolgot
 * kiszamithatova: ami szo kozepen van, ahhoz nem nyulunk.
 *
 * ─── Amit SZANDEKOSAN nem ismerunk fel ──────────────────────────────────────
 *
 * A `8)` es a `B)` hianyzik a listabol, pedig bevett szemuveges arc. Csakhogy
 * egy felsorolas nyolcadik pontja is `8)` alakban all a sor elejen - es egy
 * lista, ami mosolyogni kezd, rosszabb, mint egy hianyzo emotikon.
 */

/** A minta es a jel. A hosszabb alakok elore kerulnek - lasd a rendezest. */
const EMOTIKONOK: Array<[string, string]> = [
  [':-)', '🙂'],
  [':)', '🙂'],
  [':-D', '😀'],
  [':D', '😀'],
  [":'(", '😢'],
  [':-(', '🙁'],
  [':(', '🙁'],
  [';-)', '😉'],
  [';)', '😉'],
  [':-P', '😛'],
  [':P', '😛'],
  [':-p', '😛'],
  [':p', '😛'],
  [':-O', '😮'],
  [':O', '😮'],
  [':-o', '😮'],
  [':o', '😮'],
  [':-/', '😕'],
  [':/', '😕'],
  [':-|', '😐'],
  [':|', '😐'],
  [':-*', '😘'],
  [':*', '😘'],
  ['xD', '😆'],
  ['XD', '😆'],
  ['</3', '💔'],
  ['<3', '❤️'],
  ['-_-', '😑'],
];

const JELEK = new Map(EMOTIKONOK);

const escape = (value: string): string => value.replace(/[.*+?^${}()|[\]\\/-]/g, '\\$&');

/*
  A HOSSZABB minta nyer.

  Egy alternacio balrol jobbra illeszt, tehat ha a `:)` allna elol, a `:-)`
  soha nem illeszkedne teljesen. Hossz szerint csokkenobe rendezve viszont
  mindig a leghosszabb, vagyis a legpontosabb alak fog.
*/
const ALTERNACIO = EMOTIKONOK.map(([minta]) => minta)
  .sort((a, b) => b.length - a.length)
  .map(escape)
  .join('|');

// Sima szoveg-osszefuzes, nem sablonliteral: abban a `\s` nema modon `s`-re
// egyszerusodne, es a szabaly minden "s" betu utan illeszkedne.
const EMOTIKON_RE = new RegExp('(?:^|\\s)(' + ALTERNACIO + ')$');

/**
 * Illeszkedik-e emotikon a kurzor ELOTTI szoveg vegen.
 *
 * Kulon fuggveny, hogy a szabaly a szerkeszto felepitese nelkul is
 * ellenorizheto legyen: a hamis talalatok (`https://`, `2:00`) itt dolnek el,
 * nem a kepernyon.
 */
export function matchEmoticon(before: string): { jel: string; minta: string } | null {
  const talalat = EMOTIKON_RE.exec(before);
  const minta = talalat?.[1];
  if (!minta) return null;

  const jel = JELEK.get(minta);
  return jel ? { jel, minta } : null;
}

/**
 * Kodban NEM alakitunk at semmit.
 *
 * Egy kodblokkban a `:)` es a `<3` a kod resze - ott a csere nem
 * kenyelmi funkcio, hanem elrontja, amit a felhasznalo leirt.
 */
function kodban(state: EditorState, pos: number): boolean {
  const $pos = state.doc.resolve(pos);
  if ($pos.parent.type.spec.code) return true;
  return $pos.marks().some((mark) => mark.type.spec.code);
}

export interface EmojiInputRuleOptions {
  /**
   * Letezik-e ilyen nevu EGYEDI emoji.
   *
   * Az egyedi emoji `:nev:` alakban marad a szovegben - abbol keszit kepet a
   * megjelenites. Ha a rovidnev-szabaly lecserelne egy beepitett jelre, a
   * csapat sajat emojija sosem jutna el a masik oldalra.
   */
  hasCustomEmoji: (name: string) => boolean;
}

export function createEmojiInputRules(options: EmojiInputRuleOptions): Extension {
  return Extension.create({
    name: 'emojiInputRules',

    addInputRules() {
      return [
        // ─── Emotikonok ──────────────────────────────────────────────────────
        new InputRule({
          find: EMOTIKON_RE,
          handler: ({ state, range, match }) => {
            const talalat = matchEmoticon(match[0] ?? '');
            if (!talalat) return;
            const { jel, minta } = talalat;

            const kezdet = range.to - minta.length;
            if (kodban(state, kezdet)) return;

            state.tr.replaceWith(kezdet, range.to, state.schema.text(jel));
          },
        }),

        // ─── Rovidnevek: `:salute:` ──────────────────────────────────────────
        new InputRule({
          find: /(?:^|\s):([a-zA-Z0-9_+-]{2,40}):$/,
          handler: ({ state, range, match }) => {
            const nev = match[1];
            if (!nev) return;
            if (options.hasCustomEmoji(nev.toLowerCase())) return;

            const jel = emojiByShortcode(nev);
            if (!jel) return;

            // A ket kettospont is a lecserelendo reszhez tartozik.
            const kezdet = range.to - (nev.length + 2);
            if (kodban(state, kezdet)) return;

            state.tr.replaceWith(kezdet, range.to, state.schema.text(jel));
          },
        }),
      ];
    },
  });
}
